<template>
  <Layout>
    <div>
      <h1>Member Registration</h1>
      
      <div class="row">
        <div class="col-md-8">
          <div class="card">
            <div class="card-header">
              <h3>ISACI Membership Application</h3>
              <p class="text-muted mb-0">Join our community of researchers and professionals</p>
            </div>
            <div class="card-body">
              <!-- 步骤指示器 -->
              <div class="steps-indicator mb-4">
                <div class="step" :class="{ active: currentStep === 1, completed: currentStep > 1 }">
                  <div class="step-number">1</div>
                  <div class="step-label">Personal Information</div>
                </div>
                <div class="step-line"></div>
                <div class="step" :class="{ active: currentStep === 2, completed: currentStep > 2 }">
                  <div class="step-number">2</div>
                  <div class="step-label">Professional Details</div>
                </div>
                <div class="step-line"></div>
                <div class="step" :class="{ active: currentStep === 3, completed: currentStep > 3 }">
                  <div class="step-number">3</div>
                  <div class="step-label">Membership Type</div>
                </div>
              </div>

              <!-- 步骤1：个人信息 -->
              <div v-if="currentStep === 1" class="step-content">
                <div v-if="loading" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-2">Loading your profile information...</p>
                </div>
                <form v-else @submit.prevent="nextStep">
                  <div v-if="profile" class="alert alert-info mb-4">
                    <i class="fa fa-info-circle mr-2"></i>
                    Your profile information has been pre-filled. Please review and update as needed.
                  </div>
                  <div class="row">
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="firstName">First Name *</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          id="firstName" 
                          v-model="form.firstName"
                          required
                        >
                      </div>
                    </div>
                    <div class="col-md-6">
                      <div class="form-group">
                        <label for="lastName">Last Name *</label>
                        <input 
                          type="text" 
                          class="form-control" 
                          id="lastName" 
                          v-model="form.lastName"
                          required
                        >
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label for="email">Email Address *</label>
                    <input 
                      type="email" 
                      class="form-control" 
                      id="email" 
                      v-model="form.email"
                      required
                    >
                    <small class="form-text text-muted">This will be your username for login</small>
                  </div>
                  
                  <div class="form-group">
                    <label for="phone">Phone Number</label>
                    <input 
                      type="tel" 
                      class="form-control" 
                      id="phone" 
                      v-model="form.phone"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="country">Country *</label>
                    <CountrySelect 
                      v-model="form.country"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label for="address">Address</label>
                    <textarea 
                      class="form-control" 
                      id="address" 
                      v-model="form.address"
                      rows="3"
                    ></textarea>
                  </div>
                  
                  <div class="form-group">
                    <button type="submit" class="btn btn-primary btn-lg">
                      Next Step <i class="fa fa-arrow-right"></i>
                    </button>
                  </div>
                </form>
              </div>

              <!-- 步骤2：专业信息 -->
              <div v-if="currentStep === 2" class="step-content">
                <form @submit.prevent="nextStep">
                  <div v-if="profile" class="alert alert-info mb-4">
                    <i class="fa fa-info-circle mr-2"></i>
                    Your professional information has been pre-filled from your profile. Please review and update as needed.
                  </div>
                  <div class="form-group">
                    <label for="organization">Organization/Institution *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="organization" 
                      v-model="form.organization"
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="jobTitle">Job Title/Position *</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="jobTitle" 
                      v-model="form.jobTitle"
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="department">Department</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="department" 
                      v-model="form.department"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="researchArea">Research Area/Field of Interest *</label>
                    <select class="form-control" id="researchArea" v-model="form.researchArea" required>
                      <option value="">Select Research Area</option>
                      <option value="analytical-chemistry">Analytical Chemistry</option>
                      <option value="biochemistry">Biochemistry</option>
                      <option value="environmental-science">Environmental Science</option>
                      <option value="food-science">Food Science</option>
                      <option value="materials-science">Materials Science</option>
                      <option value="pharmaceutical">Pharmaceutical Sciences</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div class="form-group" v-if="form.researchArea === 'other'">
                    <label for="otherResearchArea">Please Specify</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      id="otherResearchArea" 
                      v-model="form.otherResearchArea"
                    >
                  </div>
                  
                  <div class="form-group">
                    <label for="education">Highest Education Level *</label>
                    <select class="form-control" id="education" v-model="form.education" required>
                      <option value="">Select Education Level</option>
                      <option value="bachelor">Bachelor's Degree</option>
                      <option value="master">Master's Degree</option>
                      <option value="phd">Ph.D.</option>
                      <option value="postdoc">Postdoctoral</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div class="form-group">
                    <label for="experience">Years of Professional Experience</label>
                    <select class="form-control" id="experience" v-model="form.experience">
                      <option value="">Select Experience Level</option>
                      <option value="0-2">0-2 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="6-10">6-10 years</option>
                      <option value="11-15">11-15 years</option>
                      <option value="15+">15+ years</option>
                    </select>
                  </div>
                  
                  <div class="form-group">
                    <button type="button" class="btn btn-secondary mr-2" @click="previousStep">
                      <i class="fa fa-arrow-left"></i> Previous
                    </button>
                    <button type="submit" class="btn btn-primary">
                      Next Step <i class="fa fa-arrow-right"></i>
                    </button>
                  </div>
                </form>
              </div>

              <!-- 步骤3：会员类型 -->
              <div v-if="currentStep === 3" class="step-content">
                <form @submit.prevent="submitRegistration">
                  <div class="form-group">
                    <label for="membershipType">Membership Type *</label>
                    <div class="membership-options">
                      <div class="card membership-option" :class="{ selected: form.membershipType === 'regular' }" @click="form.membershipType = 'regular'">
                        <div class="card-body">
                          <h5>Regular Member</h5>
                          <p class="text-muted">For professionals and researchers</p>
                          <div class="price">$50/year</div>
                          <ul class="benefits">
                            <li>Access to all ISACI events</li>
                            <li>Journal subscriptions</li>
                            <li>Networking opportunities</li>
                            <li>Professional development resources</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div class="card membership-option" :class="{ selected: form.membershipType === 'student' }" @click="form.membershipType = 'student'">
                        <div class="card-body">
                          <h5>Student Member</h5>
                          <p class="text-muted">For full-time students</p>
                          <div class="price">$25/year</div>
                          <ul class="benefits">
                            <li>All Regular member benefits</li>
                            <li>Student-specific events</li>
                            <li>Mentorship programs</li>
                            <li>Career development workshops</li>
                          </ul>
                        </div>
                      </div>
                      
                      <div class="card membership-option" :class="{ selected: form.membershipType === 'corporate' }" @click="form.membershipType = 'corporate'">
                        <div class="card-body">
                          <h5>Corporate Member</h5>
                          <p class="text-muted">For organizations and companies</p>
                          <div class="price">$200/year</div>
                          <ul class="benefits">
                            <li>All Regular member benefits</li>
                            <li>Corporate networking events</li>
                            <li>Industry partnerships</li>
                            <li>Exhibition opportunities</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label for="password">Password *</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="password" 
                      v-model="form.password"
                      required
                    >
                    <small class="form-text text-muted">Minimum 8 characters</small>
                  </div>
                  
                  <div class="form-group">
                    <label for="confirmPassword">Confirm Password *</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      id="confirmPassword" 
                      v-model="form.confirmPassword"
                      required
                    >
                  </div>
                  
                  <div class="form-group">
                    <div class="form-check">
                      <input 
                        type="checkbox" 
                        class="form-check-input" 
                        id="agreeTerms" 
                        v-model="form.agreeTerms"
                        required
                      >
                      <label class="form-check-label" for="agreeTerms">
                        I agree to the <a href="#" @click.prevent="showTerms">Terms and Conditions</a> and <a href="#" @click.prevent="showPrivacy">Privacy Policy</a> *
                      </label>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <div class="form-check">
                      <input 
                        type="checkbox" 
                        class="form-check-input" 
                        id="newsletter" 
                        v-model="form.newsletter"
                      >
                      <label class="form-check-label" for="newsletter">
                        Subscribe to our newsletter for updates and announcements
                      </label>
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <button type="button" class="btn btn-secondary mr-2" @click="previousStep">
                      <i class="fa fa-arrow-left"></i> Previous
                    </button>
                    <button type="submit" class="btn btn-primary btn-lg" :disabled="submitting">
                      {{ submitting ? 'Processing...' : 'Complete Registration' }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 侧边栏 -->
        <div class="col-md-4">
          <div class="card">
            <div class="card-header">
              <h5>Membership Benefits</h5>
            </div>
            <div class="card-body">
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="fa fa-check text-success mr-2"></i>
                  Access to exclusive research publications
                </li>
                <li class="mb-2">
                  <i class="fa fa-check text-success mr-2"></i>
                  Networking with industry experts
                </li>
                <li class="mb-2">
                  <i class="fa fa-check text-success mr-2"></i>
                  Professional development opportunities
                </li>
                <li class="mb-2">
                  <i class="fa fa-check text-success mr-2"></i>
                  Conference discounts
                </li>
                <li class="mb-2">
                  <i class="fa fa-check text-success mr-2"></i>
                  Career advancement resources
                </li>
              </ul>
            </div>
          </div>
          
          <div class="card mt-3">
            <div class="card-header">
              <h5>Contact Information</h5>
            </div>
            <div class="card-body">
              <p><strong>Email:</strong> isaci.contact@gmail.com</p>
              <p><strong>Address:</strong> 33A Pagoda Street, Singapore 059192</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import CountrySelect from '@/components/CountrySelect.vue'
import { apiService } from '@/config/api'

export default {
  name: 'MemberRegistration',
  components: {
    Layout,
    CountrySelect
  },
  data() {
    return {
      currentStep: 1,
      submitting: false,
      loading: true,
      profile: null,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        address: '',
        organization: '',
        jobTitle: '',
        department: '',
        researchArea: '',
        otherResearchArea: '',
        education: '',
        experience: '',
        membershipType: '',
        password: '',
        confirmPassword: '',
        agreeTerms: false,
        newsletter: false
      }
    }
  },
  mounted() {
    this.getProfile()
  },
  methods: {
    async getProfile() {
      try {
        this.loading = true
        const response = await apiService.auth.getProfile()
        if (response.data && response.data.code === 1 && response.data.data) {
          const profile = response.data.data
          this.profile = profile
          
          // 预填充第一步的表单字段
          this.form.firstName = profile.first_name || ''
          this.form.lastName = profile.last_name || ''
          this.form.email = profile.email || profile.username || ''
          this.form.phone = profile.telephone || profile.mobile || ''
          this.form.country = profile.country || ''
          this.form.address = profile.address || ''
          
          // 预填充第二步的表单字段
          this.form.organization = profile.organization || ''
          this.form.jobTitle = profile.job_title || ''
          this.form.department = profile.department || ''
        }
      } catch (error) {
        console.error('获取用户资料失败:', error)
      } finally {
        this.loading = false
      }
    },
    nextStep() {
      if (this.validateCurrentStep()) {
        this.currentStep++
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--
      }
    },
    validateCurrentStep() {
      if (this.currentStep === 1) {
        if (!this.form.firstName || !this.form.lastName || !this.form.email || !this.form.country) {
          alert('Please fill in all required fields')
          return false
        }
        // 验证邮箱格式
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.form.email)) {
          alert('Please enter a valid email address')
          return false
        }
      } else if (this.currentStep === 2) {
        if (!this.form.organization || !this.form.jobTitle || !this.form.researchArea || !this.form.education) {
          alert('Please fill in all required fields')
          return false
        }
        if (this.form.researchArea === 'other' && !this.form.otherResearchArea) {
          alert('Please specify your research area')
          return false
        }
      }
      return true
    },
    async submitRegistration() {
      if (!this.validateCurrentStep()) {
        return
      }
      
      if (!this.form.membershipType) {
        alert('Please select a membership type')
        return
      }
      
      if (this.form.password.length < 8) {
        alert('Password must be at least 8 characters long')
        return
      }
      
      if (this.form.password !== this.form.confirmPassword) {
        alert('Passwords do not match')
        return
      }
      
      if (!this.form.agreeTerms) {
        alert('Please agree to the Terms and Conditions')
        return
      }
      
      this.submitting = true
      
      try {
        const registrationData = {
          ...this.form,
          researchArea: this.form.researchArea === 'other' ? this.form.otherResearchArea : this.form.researchArea
        }
        
        // 这里调用实际的API
        // const response = await apiService.membership.register(registrationData)
        
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        alert('Registration successful! Welcome to ISACI!')
        this.$router.push('/login')
      } catch (error) {
        console.error('Registration failed:', error)
        alert('Registration failed. Please try again.')
      } finally {
        this.submitting = false
      }
    },
    showTerms() {
      alert('Terms and Conditions will be displayed here')
    },
    showPrivacy() {
      alert('Privacy Policy will be displayed here')
    }
  }
}
</script>

<style scoped>
.steps-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-number {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background-color: #007bff;
  color: white;
}

.step.completed .step-number {
  background-color: #28a745;
  color: white;
}

.step-label {
  font-size: 14px;
  color: #6c757d;
  text-align: center;
}

.step.active .step-label {
  color: #007bff;
  font-weight: bold;
}

.step.completed .step-label {
  color: #28a745;
}

.step-line {
  width: 60px;
  height: 2px;
  background-color: #e9ecef;
  margin: 0 15px;
  margin-top: -25px;
}

.step.completed + .step-line {
  background-color: #28a745;
}

.membership-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 15px;
}

.membership-option {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid #e9ecef;
}

.membership-option:hover {
  border-color: #007bff;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.membership-option.selected {
  border-color: #007bff;
  background-color: #f8f9ff;
}

.price {
  font-size: 24px;
  font-weight: bold;
  color: #007bff;
  margin: 15px 0;
}

.benefits {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefits li {
  padding: 5px 0;
  color: #6c757d;
}

.benefits li:before {
  content: "✓";
  color: #28a745;
  font-weight: bold;
  margin-right: 8px;
}

.form-check-label a {
  color: #007bff;
  text-decoration: none;
}

.form-check-label a:hover {
  text-decoration: underline;
}

.card-header h3 {
  margin-bottom: 5px;
  color: #2c3e50;
}

.card-header p {
  font-size: 14px;
}

@media (max-width: 768px) {
  .membership-options {
    grid-template-columns: 1fr;
  }
  
  .steps-indicator {
    flex-direction: column;
  }
  
  .step-line {
    width: 2px;
    height: 30px;
    margin: 10px 0;
  }
}
</style>
